import type { UiTagType } from "@shared/components/UiTag/index.vue";
import { t } from "@shared/utils/i18n";

/** ProductGoodsOperationStatusEnum（与后端对齐） */
export const ProductGoodsOperationStatus = {
    /** 空状态（DB DEFAULT 0） */
    // Unset: 0,
    /** 待报价 */
    PendingQuotation: 1,
    /** 待上架 */
    PendingList: 2,
    /** 运营中 */
    Operating: 3,
    /** 已暂停 */
    Stopped: 4,
} as const;

export const PRODUCT_GOODS_OPERATION_STATUS_MAP: Record<number, string> = {
    // [ProductGoodsOperationStatus.Unset]: t("空状态"),
    [ProductGoodsOperationStatus.PendingQuotation]: t("待报价"),
    [ProductGoodsOperationStatus.PendingList]: t("待上架"),
    [ProductGoodsOperationStatus.Operating]: t("运营中"),
    [ProductGoodsOperationStatus.Stopped]: t("已暂停"),
};

export function getProductGoodsOperationStatusTagType(status: number): UiTagType {
    const map: Record<number, UiTagType> = {
        // [ProductGoodsOperationStatus.Unset]: "neutral",
        [ProductGoodsOperationStatus.PendingQuotation]: "warning",
        [ProductGoodsOperationStatus.PendingList]: "info",
        [ProductGoodsOperationStatus.Operating]: "success",
        [ProductGoodsOperationStatus.Stopped]: "warning",
    };
    return map[status] || "neutral";
}

export const PRODUCT_GOODS_OPERATION_STATUS_OPTIONS = Object.entries(PRODUCT_GOODS_OPERATION_STATUS_MAP).map(([value, label]) => ({
    label,
    value: Number(value),
}));

/** ProductStatusEnum（与后端对齐：1 待审核 / 2 已通过 / 3 已拒绝） */
export const ProductStatus = {
    PendingAudit: 1,
    Approved: 2,
    Rejected: 3,
} as const;

export const PRODUCT_STATUS_MAP: Record<number, string> = {
    [ProductStatus.PendingAudit]: t("待审核"),
    [ProductStatus.Approved]: t("已通过"),
    [ProductStatus.Rejected]: t("已拒绝"),
};

export function getProductStatusTagType(status: number): UiTagType {
    const map: Record<number, UiTagType> = {
        [ProductStatus.PendingAudit]: "warning",
        [ProductStatus.Approved]: "success",
        [ProductStatus.Rejected]: "error",
    };
    return map[status] || "neutral";
}

/** ProductAmazonAsinListingStatusEnum */
export const ProductAmazonAsinListingStatus = {
    /** 空状态（无 listing / 未设置；筛选用，与运营状态「空状态」对齐） */
    Unset: -1,
    NotListed: 0,
    Listed: 1,
} as const;

export const PRODUCT_AMAZON_ASIN_LISTING_STATUS_MAP: Record<number, string> = {
    [ProductAmazonAsinListingStatus.Unset]: t("空状态"),
    [ProductAmazonAsinListingStatus.NotListed]: t("未上架"),
    [ProductAmazonAsinListingStatus.Listed]: t("已上架"),
};

export function getProductAmazonAsinListingStatusTagType(status: number): UiTagType {
    const map: Record<number, UiTagType> = {
        [ProductAmazonAsinListingStatus.Unset]: "neutral",
        [ProductAmazonAsinListingStatus.NotListed]: "neutral",
        [ProductAmazonAsinListingStatus.Listed]: "success",
    };
    return map[status] || "neutral";
}

export const PRODUCT_AMAZON_ASIN_LISTING_STATUS_OPTIONS = Object.entries(PRODUCT_AMAZON_ASIN_LISTING_STATUS_MAP).map(([value, label]) => ({
    label,
    value: Number(value),
}));

/** ProductAmazonAsinOrderStatusEnum */
export const ProductAmazonAsinOrderStatus = {
    OrderAccepting: 1,
    OrderPaused: 2,
    /** 永不供货 */
    NeverSupply: 3,
} as const;

export const PRODUCT_AMAZON_ASIN_ORDER_STATUS_MAP: Record<number, string> = {
    [ProductAmazonAsinOrderStatus.OrderAccepting]: t("正常接单"),
    [ProductAmazonAsinOrderStatus.OrderPaused]: t("暂停接单"),
    [ProductAmazonAsinOrderStatus.NeverSupply]: t("永不供货"),
};

export function getProductAmazonAsinOrderStatusTagType(status: number): UiTagType {
    const map: Record<number, UiTagType> = {
        /** 正常接单：绿色 */
        [ProductAmazonAsinOrderStatus.OrderAccepting]: "success",
        /** 暂停接单 / 永不供货：灰色 */
        [ProductAmazonAsinOrderStatus.OrderPaused]: "neutral",
        [ProductAmazonAsinOrderStatus.NeverSupply]: "neutral",
    };
    return map[status] || "neutral";
}

export const PRODUCT_AMAZON_ASIN_ORDER_STATUS_OPTIONS = Object.entries(PRODUCT_AMAZON_ASIN_ORDER_STATUS_MAP).map(([value, label]) => ({
    label,
    value: Number(value),
}));

/** AgreementStatusEnum */
export const AgreementStatus = {
    Pending: 1,
    Effective: 2,
    Expired: 3,
} as const;

export const AGREEMENT_STATUS_MAP: Record<number, string> = {
    [AgreementStatus.Pending]: t("待生效"),
    [AgreementStatus.Effective]: t("生效中"),
    [AgreementStatus.Expired]: t("已失效"),
};

export function getAgreementStatusTagType(status: number, statusLabel?: string): UiTagType {
    if (status === AgreementStatus.Pending) return "warning";
    if (status === AgreementStatus.Effective) return "success";
    if (status === AgreementStatus.Expired) return "error";

    const label = statusLabel || "";
    if (/待生效/.test(label)) return "warning";
    if (/生效中|使用中/.test(label)) return "success";
    if (/失效|过期/.test(label)) return "error";
    return "neutral";
}

export const AGREEMENT_STATUS_OPTIONS = Object.entries(AGREEMENT_STATUS_MAP).map(([value, label]) => ({
    label,
    value: Number(value),
}));

/** ProductTypeEnum */
export const ProductType = {
    Single: 1,
    Combo: 2,
    Bundle: 3,
} as const;

export const PRODUCT_TYPE_MAP: Record<number, string> = {
    [ProductType.Single]: t("普通产品"),
    [ProductType.Combo]: t("组合产品"),
    [ProductType.Bundle]: t("捆绑产品"),
};

export const PRODUCT_TYPE_OPTIONS = Object.entries(PRODUCT_TYPE_MAP).map(([value, label]) => ({
    label,
    value: Number(value),
}));

/** 产品类型语义（用于短标颜色） */
export type ProductTypeKind = "normal" | "combo" | "bundle" | "unknown";

const PRODUCT_TYPE_KIND_BY_TYPE: Record<number, ProductTypeKind> = {
    [ProductType.Single]: "normal",
    [ProductType.Combo]: "combo",
    [ProductType.Bundle]: "bundle",
};

const PRODUCT_TYPE_SHORT_LABEL: Record<ProductTypeKind, string> = {
    normal: t("普"),
    combo: t("组"),
    bundle: t("捆"),
    unknown: "",
};

/** "--" 等占位文案视为无类型 */
function normalizeProductTypeLabel(label?: string | null): string {
    const text = (label || "").trim();
    return /^-+$/.test(text) ? "" : text;
}

/** 优先按 productType 判定，其次兼容只返回文案的接口 */
export function resolveProductTypeKind(label?: string | null, type?: number | null): ProductTypeKind {
    if (type != null && PRODUCT_TYPE_KIND_BY_TYPE[type]) return PRODUCT_TYPE_KIND_BY_TYPE[type];
    const text = normalizeProductTypeLabel(label);
    if (text === t("普通产品") || text === "普通产品") return "normal";
    if (text === t("组合产品") || text === "组合产品") return "combo";
    if (text === t("捆绑产品") || text === "捆绑产品") return "bundle";
    return "unknown";
}

/** 短标文案（普 / 组 / 捆）；未知类型退化为文案首字，无文案则不展示 */
export function getProductTypeShortLabel(label?: string | null, type?: number | null): string {
    const kind = resolveProductTypeKind(label, type);
    if (PRODUCT_TYPE_SHORT_LABEL[kind]) return PRODUCT_TYPE_SHORT_LABEL[kind];
    const text = normalizeProductTypeLabel(label);
    return text ? text.slice(0, 1) : "";
}

/** ProductPackageTypeEnum */
export const ProductPackageType = {
    Single: 1,
    Multi: 2,
} as const;

export const PRODUCT_PACKAGE_TYPE_MAP: Record<number, string> = {
    [ProductPackageType.Single]: t("单包裹"),
    [ProductPackageType.Multi]: t("多包裹"),
};

export const PRODUCT_PACKAGE_TYPE_OPTIONS = Object.entries(PRODUCT_PACKAGE_TYPE_MAP).map(([value, label]) => ({
    label,
    value: Number(value),
}));

const PRODUCT_PACKAGE_TYPE_SHORT_LABEL: Record<number, string> = {
    [ProductPackageType.Single]: t("单"),
    [ProductPackageType.Multi]: t("多"),
};

export type ProductPackageTypeKind = "single" | "multi" | "unknown";

function normalizePackageTypeValue(type?: number | string | null): number | null {
    if (type == null || type === "") return null;
    const value = Number(type);
    return Number.isFinite(value) ? value : null;
}

function normalizePackageTypeLabel(label?: string | null): string {
    return String(label ?? "")
        .trim()
        .replace(/\s+/g, "");
}

/** 包裹类型判定：优先枚举值，其次文案（单包裹 / 多包裹） */
export function resolvePackageTypeKind(label?: string | null, type?: number | string | null): ProductPackageTypeKind {
    const normalizedType = normalizePackageTypeValue(type);
    if (normalizedType === ProductPackageType.Single) return "single";
    if (normalizedType === ProductPackageType.Multi) return "multi";
    const text = normalizePackageTypeLabel(label);
    if (text.includes("多包裹") || text === "多") return "multi";
    if (text.includes("单包裹") || text === "单") return "single";
    return "unknown";
}

/** 包裹类型短标（单 / 多），空值与未知类型不展示 */
export function getPackageTypeShortLabel(label?: string | null, type?: number | string | null): string {
    const kind = resolvePackageTypeKind(label, type);
    if (kind === "single" || kind === "multi") {
        return PRODUCT_PACKAGE_TYPE_SHORT_LABEL[kind === "single" ? ProductPackageType.Single : ProductPackageType.Multi];
    }
    const text = normalizePackageTypeLabel(label);
    if (text.includes("多")) return t("多");
    if (text.includes("单")) return t("单");
    return "";
}

/** UiShortTag：产品类型与包裹类型数字枚举会撞号（均为 1/2），必须带 variant 选表 */
export type ShortTagVariant = "product" | "package";

export type ShortTagKind = ProductTypeKind | ProductPackageTypeKind;

export interface ShortTagResolved {
    kind: ShortTagKind;
    text: string;
}

function toOptionalNumber(type?: number | string | null): number | null {
    if (type == null || type === "") return null;
    const value = Number(type);
    return Number.isFinite(value) ? value : null;
}

/** 按 variant 解析短标文案与 kind（空 / 未知则 text 为空，组件不渲染） */
export function resolveShortTag(variant: ShortTagVariant, label?: string | null, type?: number | string | null): ShortTagResolved {
    if (variant === "package") {
        const kind = resolvePackageTypeKind(label, type);
        return { kind, text: getPackageTypeShortLabel(label, type) };
    }
    const productType = toOptionalNumber(type);
    const kind = resolveProductTypeKind(label, productType);
    return { kind, text: getProductTypeShortLabel(label, productType) };
}

/** ProductQuotationStatusEnum */
export const ProductQuotationStatus = {
    NotApplied: 1,
    Auditing: 2,
    PendingConfirm: 3,
    Effective: 4,
    Rejected: 5,
    NotApproved: 6,
} as const;

export const PRODUCT_QUOTATION_STATUS_MAP: Record<number, string> = {
    [ProductQuotationStatus.NotApplied]: t("未申请"),
    [ProductQuotationStatus.Auditing]: t("审核中"),
    [ProductQuotationStatus.PendingConfirm]: t("待确认"),
    [ProductQuotationStatus.Effective]: t("已生效"),
    [ProductQuotationStatus.Rejected]: t("已拒绝"),
    [ProductQuotationStatus.NotApproved]: t("不通过"),
};

export const PRODUCT_QUOTATION_STATUS_OPTIONS = Object.entries(PRODUCT_QUOTATION_STATUS_MAP).map(([value, label]) => ({
    label,
    value: Number(value),
}));

/** VcTypeEnum */
export const VcType = {
    PO: "PO",
    DF: "DF",
} as const;
